/**
 * The approval disclosure (ADR-022 §4): before an admin approves an arche for
 * a workspace or a Company they see — resolved SERVER-side, never from the
 * request — the arche's name and publisher, the permissions its published
 * manifest declares (with implications), and the concrete REACH the approval
 * unlocks per data class. The approval is PINNED to exactly that: a later
 * release that declares a new permission, or a data class it newly reaches,
 * gets nothing new until someone approves again through this same dialog.
 * Revocation is immediate.
 *
 * The server refuses a decision this dialog cannot foresee, and it is shown
 * in words: an admin changing a workspace OWNER's deny (APPROVAL_OWNER_DENY).
 * Publishing an arche does not bar approving it (ADR-022 amendment
 * 2026-10-14).
 *
 * Opened two ways: from "Approve an arche…" (search first), or pre-seeded
 * with an arche (`initialArcheId`) to re-approve a denied row — the
 * disclosure is shown either way; no approval is ever recorded unseen.
 *
 * "Deny here" exists only at workspace scope: the resolver honours a
 * workspace deny (over a Company approve and Trust Fias) and nothing else,
 * and the server refuses a Company-scope deny.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ArcheSearchCombobox } from './ArcheSearchCombobox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck } from 'lucide-react';
import type { ArcheApprovalDisclosureDto, ArcheSearchDto } from '@/lib/api';
import { describeCompanyError } from '../lib/company-errors';

interface ArcheApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the approval applies — decides the copy and whether Deny is offered. */
  scopeKind: 'workspace' | 'company';
  /** "this workspace" or the Company's name, for the title. */
  scopeLabel: string;
  /** Resolves the disclosure for an arche id (server-side). */
  loadDisclosure: (archeId: string) => Promise<ArcheApprovalDisclosureDto>;
  /** Resolves a typed name (or id) to published plugin arches (server-side). */
  searchArches: (q: string) => Promise<ArcheSearchDto>;
  /** Cache scope for the typeahead — the holder or Company id. */
  scopeKey: string;
  /** Open straight on this arche's disclosure (re-approving a denied row). */
  initialArcheId?: string | null;
  onDecide: (input: {
    archeId: string;
    decision: 'approve' | 'deny';
    note?: string;
  }) => Promise<unknown>;
}

export function ArcheApprovalDialog({
  open,
  onOpenChange,
  scopeKind,
  scopeLabel,
  loadDisclosure,
  searchArches,
  scopeKey,
  initialArcheId = null,
  onDecide,
}: ArcheApprovalDialogProps) {
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [disclosure, setDisclosure] = useState<ArcheApprovalDisclosureDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Callers pass a fresh arrow each render; reading it through a ref keeps
  // the pre-seed effect keyed on open + the seed only, so a parent re-render
  // (the decide mutation going pending or failing) cannot clear an error and
  // refetch behind the user's back.
  const loadDisclosureRef = useRef(loadDisclosure);
  loadDisclosureRef.current = loadDisclosure;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setNote('');
      setDisclosure(null);
      setError(null);
      setBusy(false);
      return;
    }
    if (!initialArcheId) return;
    // Pre-seeded: straight to the disclosure. Stale answers (the dialog
    // closed or re-seeded meanwhile) are dropped.
    let live = true;
    setBusy(true);
    setError(null);
    loadDisclosureRef
      .current(initialArcheId)
      .then((d) => live && setDisclosure(d))
      .catch((err) => live && setError(describeCompanyError(err, 'Could not look that arche up.')))
      .finally(() => live && setBusy(false));
    return () => {
      live = false;
    };
  }, [open, initialArcheId]);

  async function openDisclosure(archeId: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      setDisclosure(await loadDisclosure(archeId));
    } catch (err) {
      setError(describeCompanyError(err, 'Could not look that arche up.'));
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: 'approve' | 'deny'): Promise<void> {
    if (!disclosure) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide({ archeId: disclosure.archeId, decision, note: note.trim() || undefined });
      onOpenChange(false);
    } catch (err) {
      setError(describeCompanyError(err, 'Could not record that decision.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            Approve an arche for {scopeLabel}
          </DialogTitle>
          <DialogDescription>
            An approved arche can read{' '}
            {scopeKind === 'workspace' ? 'this workspace’s' : 'every linked workspace’s'} data on
            behalf of its members, at each member’s own role, without picking documents one by one.
            You can revoke at any time; revocation is immediate.
          </DialogDescription>
        </DialogHeader>

        {!disclosure ? (
          <DialogBody>
            <div className="grid gap-1.5">
              <Label htmlFor="approve-arche-query">Arche name or id</Label>
              <ArcheSearchCombobox
                id="approve-arche-query"
                scopeKey={scopeKey}
                search={searchArches}
                value={query}
                onValueChange={setQuery}
                onSelect={(r) => void openDisclosure(r.archeId)}
                disabled={busy}
                companyScope={scopeKind === 'company' ? { name: scopeLabel } : undefined}
              />
              <p className="text-xs text-muted-foreground">
                {scopeKind === 'company'
                  ? `Arches ${scopeLabel} owns or was granted are listed first. Type a name to search published arches, or paste an arc_… id.`
                  : 'Start typing a name to pick from published arches, or paste an arc_… id.'}
              </p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              {busy && <span className="self-center text-sm text-muted-foreground">Loading…</span>}
            </DialogFooter>
          </DialogBody>
        ) : (
          <>
            <DialogBody className="text-sm">
              <div>
                <p className="font-medium text-foreground">{disclosure.name}</p>
                <p className="text-muted-foreground">
                  Published by{' '}
                  {disclosure.isPlatformOwned
                    ? 'Fias'
                    : disclosure.contributorId
                      ? `user …${disclosure.contributorId.slice(-6)}`
                      : 'an unknown publisher'}
                </p>
              </div>

              <div>
                <p className="font-medium text-foreground">What it will be able to read here</p>
                {disclosure.reach.length === 0 ? (
                  <p className="text-muted-foreground">
                    Nothing beyond what it already can — this arche declares no permission that an
                    approval widens.
                  </p>
                ) : (
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {disclosure.reach.map((r) => (
                      <li key={r.entityId}>{r.sentence}</li>
                    ))}
                  </ul>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  The approval covers exactly this. If a later release asks for more, the new reach
                  is refused until someone approves again.
                </p>
              </div>

              <div>
                <p className="font-medium text-foreground">Permissions it declares</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {disclosure.permissions.map((p) => (
                    <Badge key={p} variant="secondary">
                      {p}
                    </Badge>
                  ))}
                  {disclosure.impliedPermissions.map((p) => (
                    <Badge
                      key={`implied-${p}`}
                      variant="outline"
                      title="Implied by a declared permission"
                    >
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="approve-note">Note (optional)</Label>
                <Input
                  id="approve-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  placeholder={
                    scopeKind === 'workspace'
                      ? 'Why this arche is approved (or denied) here'
                      : 'Why this arche is approved here'
                  }
                  disabled={busy}
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}
            </DialogBody>
            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => (initialArcheId ? onOpenChange(false) : setDisclosure(null))}
                disabled={busy}
              >
                {initialArcheId ? 'Cancel' : 'Back'}
              </Button>
              {scopeKind === 'workspace' && (
                <Button variant="outline" onClick={() => decide('deny')} disabled={busy}>
                  Deny here
                </Button>
              )}
              <Button onClick={() => decide('approve')} disabled={busy}>
                Approve
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
